import { useMemo } from "react";
import { useSearchParams } from "react-router";

import {
  EmptystateSearchNoResultsColored,
  EmptystateUsedByColored,
} from "components/icons/generated";
import SearchInput from "components/SearchInput";
import { SEARCH_PLACEHOLDERS } from "constants/searchPlaceholders";
import { URL_SEARCH_KEY } from "constants/urlQueryKeys";
import EmptyState from "ds/components/EmptyState";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import { useInitialSort } from "ds/components/Table/hooks/useInitialSort";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import useTypedContext from "hooks/useTypedContext";
import { fuzzySearch } from "utils/fuzzySearch";

import ModuleVersionPageIntoTitle from "../../components/PageIntoTitle";
import ModuleVersionScrollableWrapper from "../../components/ScrollableWrapper";
import { VersionContext } from "../../Context";
import { FILTERS_ORDER_SETTINGS_KEY_VERSION_CONSUMERS } from "./constants";
import { MODULE_VERSION_CONSUMERS_TABLE_CELLS } from "./Table/Cells";
import {
  INITIAL_SORT_DIRECTION,
  INITIAL_SORT_OPTION,
  MODULE_VERSION_CONSUMERS_TABLE_FILTERS_STORAGE_KEY,
  SORT_CONFIG,
  NO_STICKY_RIGHT,
} from "./Table/constants";
import { useSortingModuleVersionConsumers } from "./Table/useSortingModuleVersionConsumers";
import useModuleVersionConsumersTableStore from "./Table/useTableStore";

const ModuleVersionConsumers = () => {
  const { version } = useTypedContext(VersionContext);

  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get(URL_SEARCH_KEY);
  const trackAnalytics = useAnalytics();

  useInitialSort(INITIAL_SORT_OPTION, INITIAL_SORT_DIRECTION);

  const filteredConsumers = useMemo(() => {
    const rows = (version?.consumers || []).map((consumer) => ({
      id: consumer.id,
      name: consumer.name,
      space: consumer.spaceDetails.name,
      spaceId: consumer.spaceDetails.id,
    }));

    if (searchQuery) {
      return fuzzySearch(rows, searchQuery.trim(), {
        keys: ["name"],
        scoreThreshold: -1000,
      });
    }

    return rows;
  }, [version?.consumers, searchQuery]);

  const sortedConsumers = useSortingModuleVersionConsumers(filteredConsumers);

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Consumers"
          tooltip="Stacks that are using this module."
        />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder={SEARCH_PLACEHOLDERS.CONSUMERS}
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_CONSUMERS}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper padding="0">
        {sortedConsumers.length === 0 && (
          <EmptyState
            padding="x-large"
            icon={searchQuery ? EmptystateSearchNoResultsColored : EmptystateUsedByColored}
            caption={
              searchQuery ? "No consumers match your search query" : "This module has no consumers"
            }
            fullHeight
            announce={!!searchQuery}
          />
        )}
        {sortedConsumers.length > 0 && (
          <TableSimple
            data={sortedConsumers}
            store={useModuleVersionConsumersTableStore}
            ariaLabel="Consumers"
            trackAnalytics={trackAnalytics}
            cellPerColumn={MODULE_VERSION_CONSUMERS_TABLE_CELLS}
            filtersStorageKey={MODULE_VERSION_CONSUMERS_TABLE_FILTERS_STORAGE_KEY}
            sortConfig={SORT_CONFIG}
            hasPagination={false}
            hideSettings
            stickyRight={NO_STICKY_RIGHT}
          />
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionConsumers;
